import { useState, useEffect } from 'react';
import { colors } from '../constants/colors';
import { getShippingConfig, updateShippingConfig, verifyOriginAddress, getCarrierServices, testShippingRate } from '../api/client';
import type { TestRateResponse } from '../api/client';
import type { AddressVerificationResponse } from '../api/client';
import type {
  ShippingStrategy,
  ShippingCarrier,
  LabelFormat,
  UpdateShippingConfigRequest,
  CarrierServiceOption,
} from '../types';
import { isAlaskaOrHawaii } from '../utils/shippingCalculator';
import { Package, Truck, DollarSign, Save, Loader2, MapPin, Printer, X, AlertTriangle, ChevronRight, Check, FlaskConical } from 'lucide-react';

// ── Toggle Switch ───────────────────────────────────────────────────

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      type="button"
      style={{
        position: 'relative',
        width: '36px',
        height: '22px',
        borderRadius: '11px',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        backgroundColor: checked ? '#5F3DC4' : '#D6D4E2',
        transition: 'background-color 0.2s',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '16px' : '2px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.2s',
        }}
      />
    </button>
  );
}

/** Format a money string to 2 decimal places on blur. */
function formatMoney(value: string): string {
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return n.toFixed(2);
}

// ── Constants ───────────────────────────────────────────────────────

interface ShippingSettingsSectionProps {
  onSaved?: () => void;
}

type Section = 'pricing' | 'address' | 'label';

const CARRIER_OPTIONS: { value: ShippingCarrier; label: string; logo: string }[] = [
  { value: 'USPS', label: 'USPS', logo: '/img/logos/carrier-usps.svg' },
  { value: 'FEDEX', label: 'FedEx', logo: '/img/logos/carrier-fx.svg' },
  { value: 'UPS', label: 'UPS', logo: '/img/logos/carrier-ups.svg' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// ── Component ───────────────────────────────────────────────────────

export function ShippingSettingsSection({ onSaved }: ShippingSettingsSectionProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Section navigation
  const [section, setSection] = useState<Section>('pricing');

  // Form state
  const [strategy, setStrategy] = useState<ShippingStrategy>('FLAT_RATE');
  const [flatRateFirstItemAmount, setFlatRateFirstItemAmount] = useState('9.99');
  const [flatRateAdditionalItemAmount, setFlatRateAdditionalItemAmount] = useState('2.00');
  const [originZip, setOriginZip] = useState('');
  const [carrier, setCarrier] = useState<ShippingCarrier>('USPS');
  const [carrierService, setCarrierService] = useState('');
  const [rateCardGeneratedAt, setRateCardGeneratedAt] = useState<string | null>(null);
  const [availableServices, setAvailableServices] = useState<CarrierServiceOption[]>([]);
  const [freeShippingThresholdEnabled, setFreeShippingThresholdEnabled] = useState(false);
  const [freeShippingThresholdAmount, setFreeShippingThresholdAmount] = useState('100.00');
  const [maxShippingCapEnabled, setMaxShippingCapEnabled] = useState(false);
  const [maxShippingCapAmount, setMaxShippingCapAmount] = useState('25.00');

  // Origin address
  const [originName, setOriginName] = useState('');
  const [originStreet1, setOriginStreet1] = useState('');
  const [originStreet2, setOriginStreet2] = useState('');
  const [originCity, setOriginCity] = useState('');
  const [originState, setOriginState] = useState('');
  const [originPhone, setOriginPhone] = useState('');

  // Label format
  const [labelFormat, setLabelFormat] = useState<LabelFormat>('LETTER');

  // Address verification
  const [isVerifying, setIsVerifying] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState<AddressVerificationResponse | null>(null);

  // Test rates modal
  const [showTestRates, setShowTestRates] = useState(false);
  const [testZip, setTestZip] = useState('');
  const [testWeightOz, setTestWeightOz] = useState('16');
  const [testResponse, setTestResponse] = useState<TestRateResponse | null>(null);
  const [isTestingRate, setIsTestingRate] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const loadCarrierServices = async (carrierToLoad?: string) => {
    try {
      const services = await getCarrierServices(carrierToLoad);
      setAvailableServices(services);
    } catch {
      // Carrier services not available yet — not critical
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const config = await getShippingConfig();

      setStrategy(config.strategy);
      setFlatRateFirstItemAmount(config.flatRateFirstItemAmount?.toString() || config.flatRateAmount?.toString() || '9.99');
      setFlatRateAdditionalItemAmount(config.flatRateAdditionalItemAmount?.toString() || '2.00');
      setOriginZip(config.originZip || '');
      setCarrier(config.carrier || 'USPS');
      setCarrierService(config.carrierService || '');
      setRateCardGeneratedAt(config.rateCardGeneratedAt || null);
      setFreeShippingThresholdEnabled(config.freeShippingThresholdEnabled);
      setFreeShippingThresholdAmount(config.freeShippingThresholdAmount?.toString() || '100.00');
      setMaxShippingCapEnabled(config.maxShippingCapEnabled);
      setMaxShippingCapAmount(config.maxShippingCapAmount?.toString() || '25.00');

      setOriginName(config.originName || '');
      setOriginStreet1(config.originStreet1 || '');
      setOriginStreet2(config.originStreet2 || '');
      setOriginCity(config.originCity || '');
      setOriginState(config.originState || '');
      setOriginPhone(config.originPhone || '');

      setLabelFormat(config.labelFormat || 'LETTER');

      // Load carrier services
      if (config.carrier) {
        loadCarrierServices(config.carrier);
      }
    } catch {
      // Use defaults if no config exists
    } finally {
      setIsLoading(false);
    }
  };

  const buildRequest = (overrideAddress?: {
    street1: string; street2?: string; city: string; state: string; zip: string;
  }): UpdateShippingConfigRequest => ({
    strategy,
    flatRateAmount: strategy === 'FLAT_RATE' ? parseFloat(flatRateFirstItemAmount) : undefined,
    flatRateFirstItemAmount: strategy === 'FLAT_RATE' ? parseFloat(flatRateFirstItemAmount) : undefined,
    flatRateAdditionalItemAmount: strategy === 'FLAT_RATE' ? parseFloat(flatRateAdditionalItemAmount) : undefined,
    originZip: overrideAddress?.zip || originZip || undefined,
    carrier: strategy === 'WEIGHT_BASED' ? carrier : undefined,
    carrierService: strategy === 'WEIGHT_BASED' ? carrierService || undefined : undefined,
    freeShippingThresholdEnabled: strategy !== 'FREE' ? freeShippingThresholdEnabled : false,
    freeShippingThresholdAmount: strategy !== 'FREE' && freeShippingThresholdEnabled ? parseFloat(freeShippingThresholdAmount) : undefined,
    maxShippingCapEnabled: strategy !== 'FREE' ? maxShippingCapEnabled : false,
    maxShippingCapAmount: strategy !== 'FREE' && maxShippingCapEnabled ? parseFloat(maxShippingCapAmount) : undefined,
    originName: originName || undefined,
    originStreet1: overrideAddress?.street1 || originStreet1 || undefined,
    originStreet2: overrideAddress?.street2 || originStreet2 || undefined,
    originCity: overrideAddress?.city || originCity || undefined,
    originState: overrideAddress?.state || originState || undefined,
    originPhone: originPhone || undefined,
    labelFormat,
  });

  const validate = (): string | null => {
    if (strategy === 'FLAT_RATE') {
      const first = parseFloat(flatRateFirstItemAmount);
      if (isNaN(first) || first < 0) return 'Please enter a valid first-item shipping rate';
      const additional = parseFloat(flatRateAdditionalItemAmount);
      if (isNaN(additional) || additional < 0) return 'Please enter a valid additional-item shipping rate';
    }
    if (strategy === 'WEIGHT_BASED') {
      if (!originZip || !/^\d{5}(-\d{4})?$/.test(originZip)) return 'Please enter a valid origin ZIP code';
      if (isAlaskaOrHawaii(originZip)) {
        return 'Weight-based shipping is only available for CONUS (contiguous US) origins';
      }
      if (!carrierService) {
        return 'Please select a service level for the carrier';
      }
    }
    if (freeShippingThresholdEnabled) {
      const amount = parseFloat(freeShippingThresholdAmount);
      if (isNaN(amount) || amount <= 0) return 'Please enter a valid free shipping threshold';
    }
    if (maxShippingCapEnabled) {
      const amount = parseFloat(maxShippingCapAmount);
      if (isNaN(amount) || amount <= 0) return 'Please enter a valid max shipping cap';
    }
    return null;
  };

  const doSave = async (overrideAddress?: {
    street1: string; street2?: string; city: string; state: string; zip: string;
  }) => {
    try {
      setIsSaving(true);
      setError(null);
      await updateShippingConfig(buildRequest(overrideAddress));
      if (overrideAddress) {
        setOriginStreet1(overrideAddress.street1);
        setOriginStreet2(overrideAddress.street2 || '');
        setOriginCity(overrideAddress.city);
        setOriginState(overrideAddress.state);
        setOriginZip(overrideAddress.zip);
      }
      setSuccessMessage('Shipping settings saved successfully');
      setAddressSuggestion(null);
      onSaved?.();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save shipping settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    // If origin address is filled, verify it first
    const hasOriginAddress = originStreet1.trim() && originCity.trim() && originState.trim() && originZip.trim();
    if (hasOriginAddress) {
      try {
        setIsVerifying(true);
        const result = await verifyOriginAddress({
          street1: originStreet1.trim(),
          street2: originStreet2.trim() || undefined,
          city: originCity.trim(),
          state: originState.trim(),
          zip: originZip.trim(),
        });
        if (result.matched) {
          await doSave();
        } else {
          setAddressSuggestion(result);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to verify address');
      } finally {
        setIsVerifying(false);
      }
    } else {
      await doSave();
    }
  };

  const handleUseOriginal = () => {
    if (!addressSuggestion) return;
    const o = addressSuggestion.original;
    doSave({ street1: o.streetLine1, street2: o.streetLine2 || undefined, city: o.city, state: o.state, zip: o.zipCode });
  };

  const handleUseSuggested = () => {
    if (!addressSuggestion) return;
    const s = addressSuggestion.suggested;
    doSave({ street1: s.streetLine1, street2: s.streetLine2 || undefined, city: s.city, state: s.state, zip: s.zipCode });
  };

  // ── Sidebar status summaries ──────────────────────────────────────

  const getPricingStatus = (): string => {
    const parts: string[] = [];
    if (strategy === 'FREE') parts.push('Free shipping');
    else if (strategy === 'FLAT_RATE') parts.push('Flat rate');
    else if (strategy === 'WEIGHT_BASED') {
      parts.push('Weight-based');
      const carrierLabel = CARRIER_OPTIONS.find(c => c.value === carrier)?.label || carrier;
      const svcLabel = availableServices.find(s => s.serviceCode === carrierService)?.displayName;
      if (svcLabel) parts.push(`${carrierLabel} ${svcLabel}`);
      else parts.push(carrierLabel);
    }
    if (freeShippingThresholdEnabled && strategy !== 'FREE') {
      parts.push(`Free over $${parseFloat(freeShippingThresholdAmount).toFixed(0)}`);
    }
    return parts.join(' \u00b7 ');
  };

  const getAddressStatus = (): string => {
    if (originName && originCity && originState) {
      return `${originName} \u00b7 ${originCity}, ${originState}`;
    }
    if (originCity && originState) return `${originCity}, ${originState}`;
    return 'Not configured';
  };

  const getLabelStatus = (): string => {
    return labelFormat === 'THERMAL' ? 'Thermal 4\u00d76' : 'Letter 8.5\u00d711';
  };

  // ── Sidebar items config ──────────────────────────────────────────

  const sidebarItems: { key: Section; label: string; status: string; Icon: typeof Truck }[] = [
    { key: 'pricing', label: 'Shipping Pricing', status: getPricingStatus(), Icon: Truck },
    { key: 'address', label: 'Return Address', status: getAddressStatus(), Icon: MapPin },
    { key: 'label', label: 'Label Format', status: getLabelStatus(), Icon: Printer },
  ];

  // ── Loading state ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.pageTitle}>Shipping</h2>
            <p style={styles.pageSubtitle}>Set how orders are priced, packed, and shipped from your store.</p>
          </div>
        </div>
        <div style={styles.loadingState}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Loading shipping settings...</span>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div style={styles.page}>
      {/* Page header */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.pageTitle}>Shipping</h2>
          <p style={styles.pageSubtitle}>Set how orders are priced, packed, and shipped from your store.</p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isVerifying || (strategy === 'WEIGHT_BASED' && isAlaskaOrHawaii(originZip))}
          style={{
            ...styles.saveButton,
            opacity: (isSaving || isVerifying) ? 0.7 : 1,
          }}
        >
          {isSaving || isVerifying ? (
            <>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              {isVerifying ? 'Verifying...' : 'Saving...'}
            </>
          ) : (
            <>
              <Save size={16} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}
      {successMessage && <div style={styles.successBanner}>{successMessage}</div>}

      {/* Sidebar + Pane layout */}
      <div style={styles.layout}>
        {/* Left rail */}
        <div style={styles.sidebar}>
          {sidebarItems.map(({ key, label, status, Icon }) => {
            const active = section === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSection(key)}
                style={{
                  ...styles.sidebarItem,
                  ...(active ? styles.sidebarItemActive : {}),
                }}
              >
                <div
                  style={{
                    ...styles.sidebarIcon,
                    backgroundColor: active ? '#5F3DC4' : colors.gray[100],
                  }}
                >
                  <Icon size={18} color={active ? '#FFFFFF' : colors.gray[600]} />
                </div>
                <div style={styles.sidebarText}>
                  <span style={{
                    ...styles.sidebarLabel,
                    color: active ? colors.ink.black : colors.gray[700],
                  }}>
                    {label}
                  </span>
                  <span style={styles.sidebarStatus}>{status}</span>
                </div>
                <ChevronRight size={16} color={colors.gray[400]} style={{ flexShrink: 0 }} />
              </button>
            );
          })}
        </div>

        {/* Right pane */}
        <div style={styles.pane}>
          {section === 'pricing' && renderPricingPane()}
          {section === 'address' && renderAddressPane()}
          {section === 'label' && renderLabelPane()}
        </div>
      </div>

      {/* Address Verification Modal */}
      {addressSuggestion && (
        <div style={styles.modalOverlay} onClick={() => setAddressSuggestion(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Confirm Return Address</h2>
              <button onClick={() => setAddressSuggestion(null)} style={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.confirmText}>
                We found a suggested address. Which would you like to use?
              </p>
              <div style={styles.comparisonRow}>
                <div style={styles.addressOption}>
                  <div style={styles.addressOptionLabel}>You entered</div>
                  <div style={styles.addressCard}>
                    <div style={styles.addressLine}>{addressSuggestion.original.streetLine1}</div>
                    {addressSuggestion.original.streetLine2 && (
                      <div style={styles.addressLine}>{addressSuggestion.original.streetLine2}</div>
                    )}
                    <div style={styles.addressLine}>
                      {addressSuggestion.original.city}, {addressSuggestion.original.state} {addressSuggestion.original.zipCode}
                    </div>
                  </div>
                  <button onClick={handleUseOriginal} style={styles.secondaryButton} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Use Original'}
                  </button>
                </div>
                <div style={styles.addressOption}>
                  <div style={styles.addressOptionLabel}>Suggested</div>
                  <div style={{ ...styles.addressCard, ...styles.suggestedCard }}>
                    <div style={
                      addressSuggestion.original.streetLine1.toLowerCase() !== addressSuggestion.suggested.streetLine1.toLowerCase()
                        ? styles.diffLine : styles.addressLine
                    }>
                      {addressSuggestion.suggested.streetLine1}
                    </div>
                    {addressSuggestion.suggested.streetLine2 && (
                      <div style={styles.addressLine}>{addressSuggestion.suggested.streetLine2}</div>
                    )}
                    <div style={
                      addressSuggestion.original.city.toLowerCase() !== addressSuggestion.suggested.city.toLowerCase() ||
                      addressSuggestion.original.state.toLowerCase() !== addressSuggestion.suggested.state.toLowerCase() ||
                      addressSuggestion.original.zipCode !== addressSuggestion.suggested.zipCode
                        ? styles.diffLine : styles.addressLine
                    }>
                      {addressSuggestion.suggested.city}, {addressSuggestion.suggested.state} {addressSuggestion.suggested.zipCode}
                    </div>
                  </div>
                  <button onClick={handleUseSuggested} style={styles.primaryButton} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Use Suggested'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Rates Modal */}
      {showTestRates && (
        <div style={styles.modalOverlay} onClick={() => setShowTestRates(false)}>
          <div style={{ ...styles.modal, maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Test Shipping Rates</h2>
              <button onClick={() => setShowTestRates(false)} style={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={{
                display: 'flex',
                gap: '12px',
                padding: '10px 14px',
                backgroundColor: colors.gray[100],
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '12px',
                color: colors.gray[600],
              }}>
                <div><span style={{ fontWeight: 600, color: colors.ink.black }}>Carrier:</span> {CARRIER_OPTIONS.find(c => c.value === carrier)?.label || carrier}</div>
                <div><span style={{ fontWeight: 600, color: colors.ink.black }}>Service:</span> {availableServices.find(s => s.serviceCode === carrierService)?.displayName || carrierService}</div>
              </div>

              <p style={{ fontSize: '13px', color: colors.gray[600], margin: '0 0 16px', lineHeight: '1.5' }}>
                Enter a destination ZIP and item weight to get a live rate from the carrier.
              </p>

              <div style={{ marginBottom: '12px' }}>
                <label style={styles.fieldLabel}>Buyer ZIP code</label>
                <input
                  type="text"
                  value={testZip}
                  onChange={(e) => { setTestZip(e.target.value); setTestResponse(null); setTestError(null); }}
                  style={styles.input}
                  placeholder="e.g. 10001"
                  maxLength={5}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={styles.fieldLabel}>Item weight (oz)</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={testWeightOz}
                  onChange={(e) => { setTestWeightOz(e.target.value); setTestResponse(null); setTestError(null); }}
                  style={styles.input}
                  placeholder="16"
                />
              </div>

              <button
                type="button"
                disabled={!testZip || testZip.length < 5 || !originZip || isTestingRate}
                onClick={async () => {
                  setIsTestingRate(true);
                  setTestError(null);
                  setTestResponse(null);
                  try {
                    const result = await testShippingRate({
                      originZip: originZip.length > 5 ? originZip.substring(0, 5) : originZip,
                      destZip: testZip,
                      weightOz: parseFloat(testWeightOz) || 16,
                      carrier,
                      carrierService,
                    });
                    setTestResponse(result);
                  } catch (e) {
                    setTestError(e instanceof Error ? e.message : 'Failed to get rates');
                  } finally {
                    setIsTestingRate(false);
                  }
                }}
                style={{
                  ...styles.saveButton,
                  width: '100%',
                  maxWidth: 'none',
                  opacity: (!testZip || testZip.length < 5 || isTestingRate) ? 0.5 : 1,
                }}
              >
                {isTestingRate ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Getting rates...
                  </>
                ) : 'Get Rate'}
              </button>

              {testError && (
                <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#DC2626' }}>
                  {testError}
                </div>
              )}

              {testResponse && (
                <div style={{ marginTop: '16px' }}>
                  {testResponse.matched ? (
                    <div style={{
                      padding: '16px',
                      backgroundColor: colors.gray[100],
                      borderRadius: '10px',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: '12px', color: colors.gray[600], marginBottom: '4px' }}>
                        {testResponse.carrier} {testResponse.service}
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: 700, color: colors.ink.black }}>
                        ${testResponse.rate?.toFixed(2)}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: '12px 14px',
                      backgroundColor: '#FEF3C7',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#92400E',
                      lineHeight: '1.5',
                    }}>
                      No rate found for {testResponse.carrier} {testResponse.service}.
                      {testResponse.allRates.length > 0 && ' See available rates below.'}
                    </div>
                  )}

                  {testResponse.allRates.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: colors.gray[600], marginBottom: '8px' }}>
                        All available rates
                      </div>
                      <div style={{ border: `1px solid ${colors.gray[300]}`, borderRadius: '8px', overflow: 'hidden' }}>
                        {testResponse.allRates.map((r, i) => (
                          <div key={`${r.carrier}-${r.service}`} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            fontSize: '13px',
                            borderBottom: i < testResponse.allRates.length - 1 ? `1px solid ${colors.gray[200]}` : 'none',
                            backgroundColor: (r.carrier === testResponse.carrier && r.service === testResponse.service) ? 'rgba(95, 61, 196, 0.04)' : 'transparent',
                          }}>
                            <span style={{ color: colors.gray[600] }}>{r.carrier} · {r.service}</span>
                            <span style={{ fontWeight: 600, color: colors.ink.black }}>${r.rate.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p style={{
                fontSize: '12px',
                color: colors.gray[600],
                margin: '16px 0 0',
                lineHeight: '1.5',
                fontStyle: 'italic',
              }}>
                Rates are not exact and are our best estimate. Final charges may vary slightly.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Pane renderers ────────────────────────────────────────────────

  function renderPricingPane() {
    return (
      <>
        <div style={styles.paneHeader}>
          <div style={styles.paneIconCircle}>
            <Truck size={20} color="#FFFFFF" />
          </div>
          <div>
            <h3 style={styles.paneTitle}>Shipping Pricing</h3>
            <p style={styles.paneDesc}>Control how shipping costs are calculated and what buyers pay at checkout.</p>
          </div>
        </div>

        <div style={styles.pricingGrid}>
          {/* Left: Pricing strategy */}
          <div>
            <div style={styles.stepHeader}>
              <div>
                <div style={styles.stepTitle}>Pricing strategy</div>
                <div style={styles.stepDesc}>How buyers are charged by default.</div>
              </div>
            </div>

            <div style={styles.strategyCards}>
              {renderStrategyCard('FREE', Package, 'Free Shipping', 'You absorb the cost. Buyers see $0 shipping at checkout.')}

              {renderStrategyCard('FLAT_RATE', DollarSign, 'Flat Rate', 'Charge a set price per item. First item pays one rate, each additional pays another.')}
              {strategy === 'FLAT_RATE' && (
                <div style={styles.strategyDrawer}>
                  <div style={styles.drawerField}>
                    <label style={styles.fieldLabel}>First item rate</label>
                    <div style={styles.inputWithPrefix}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={flatRateFirstItemAmount}
                        onChange={(e) => setFlatRateFirstItemAmount(e.target.value)}
                        onBlur={() => setFlatRateFirstItemAmount(formatMoney(flatRateFirstItemAmount))}
                        style={styles.inputWithPrefixField}
                        placeholder="9.99"
                      />
                    </div>
                  </div>
                  <div style={styles.drawerField}>
                    <label style={styles.fieldLabel}>Additional item rate</label>
                    <div style={styles.inputWithPrefix}>
                      <span style={styles.inputPrefix}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={flatRateAdditionalItemAmount}
                        onChange={(e) => setFlatRateAdditionalItemAmount(e.target.value)}
                        onBlur={() => setFlatRateAdditionalItemAmount(formatMoney(flatRateAdditionalItemAmount))}
                        style={styles.inputWithPrefixField}
                        placeholder="2.00"
                      />
                    </div>
                    <span style={styles.fieldHelper}>Each item after the first in a show.</span>
                  </div>
                </div>
              )}

              {renderStrategyCard('WEIGHT_BASED', Truck, 'Weight-Based', 'Calculate live rates from your carrier based on item weight and ZIP.')}
              {strategy === 'WEIGHT_BASED' && (
                <div style={styles.strategyDrawer}>
                  <div style={styles.drawerField}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={styles.fieldLabel}>Carrier &amp; service</span>
                      {rateCardGeneratedAt && (
                        <span style={styles.rateMeta}>Updated {new Date(rateCardGeneratedAt).toLocaleDateString()}</span>
                      )}
                    </div>

                    <div style={styles.carrierRow}>
                      {CARRIER_OPTIONS.map((opt) => {
                        const active = carrier === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setCarrier(opt.value);
                              setCarrierService('');
                              loadCarrierServices(opt.value);
                            }}
                            style={{
                              ...styles.carrierCard,
                              ...(active ? styles.carrierCardActive : {}),
                            }}
                          >
                            <img src={opt.logo} alt={opt.label} style={{ width: '40px', height: '28px', objectFit: 'contain' }} />
                            <span style={{ fontSize: '14px', fontWeight: 600, color: colors.ink.black }}>{opt.label}</span>
                            {active && <Check size={16} color="#5F3DC4" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={styles.drawerField}>
                    <label style={styles.fieldLabel}>Service Level</label>
                    {availableServices.length > 0 ? (
                      <select
                        value={carrierService}
                        onChange={(e) => setCarrierService(e.target.value)}
                        style={styles.select}
                      >
                        <option value="">Select a service...</option>
                        {availableServices.map((svc) => (
                          <option key={svc.serviceCode} value={svc.serviceCode}>
                            {svc.displayName}
                            {svc.estDeliveryDaysMin && svc.estDeliveryDaysMax
                              ? ` (${svc.estDeliveryDaysMin}\u2013${svc.estDeliveryDaysMax} days)`
                              : svc.estDeliveryDaysMax
                                ? ` (~${svc.estDeliveryDaysMax} days)`
                                : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={styles.fieldHelper}>
                        No services available yet. An admin needs to refresh carrier services.
                      </div>
                    )}
                    <span style={styles.fieldHelper}>
                      Rates are calculated automatically based on item weight, buyer location, and your origin ZIP.
                    </span>
                  </div>

                  {originZip && isAlaskaOrHawaii(originZip) && (
                    <div style={styles.akHiWarning}>
                      <AlertTriangle size={16} />
                      <span>Weight-based shipping is only available for CONUS (contiguous US) origins. Alaska and Hawaii are not currently supported.</span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => { setTestResponse(null); setTestError(null); setShowTestRates(true); }}
                    style={styles.testRatesLink}
                  >
                    <FlaskConical size={14} />
                    Test rates
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right: Discounts & max shipping */}
          <div>
            <div style={styles.stepHeader}>
              <div>
                <div style={styles.stepTitle}>Discounts &amp; max shipping</div>
              </div>
            </div>

            {/* Free shipping threshold toggle */}
            <div style={{
              ...styles.toggleCard,
              ...(freeShippingThresholdEnabled ? styles.toggleCardActive : {}),
            }}>
              <div style={styles.toggleCardHeader}>
                <div style={{ flex: 1 }}>
                  <div style={styles.toggleCardTitle}>Free shipping over a threshold</div>
                  <div style={styles.toggleCardDesc}>
                    Buyers who spend more than this amount on items get free shipping on their whole order.
                  </div>
                </div>
                <ToggleSwitch
                  checked={freeShippingThresholdEnabled}
                  onChange={setFreeShippingThresholdEnabled}
                />
              </div>
              {freeShippingThresholdEnabled && (
                <div style={styles.toggleCardBody}>
                  <label style={styles.fieldLabel}>Threshold amount</label>
                  <div style={styles.inputWithPrefix}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={freeShippingThresholdAmount}
                      onChange={(e) => setFreeShippingThresholdAmount(e.target.value)}
                      onBlur={() => setFreeShippingThresholdAmount(formatMoney(freeShippingThresholdAmount))}
                      style={styles.inputWithPrefixField}
                      placeholder="100.00"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Max cap toggle */}
            <div style={{
              ...styles.toggleCard,
              ...(maxShippingCapEnabled ? styles.toggleCardActive : {}),
              marginTop: '12px',
            }}>
              <div style={styles.toggleCardHeader}>
                <div style={{ flex: 1 }}>
                  <div style={styles.toggleCardTitle}>Max shipping per order</div>
                  <div style={styles.toggleCardDesc}>
                    Buyers won't pay more than this amount for shipping on a single order.
                  </div>
                </div>
                <ToggleSwitch
                  checked={maxShippingCapEnabled}
                  onChange={setMaxShippingCapEnabled}
                />
              </div>
              {maxShippingCapEnabled && (
                <div style={styles.toggleCardBody}>
                  <label style={styles.fieldLabel}>Max amount</label>
                  <div style={styles.inputWithPrefix}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={maxShippingCapAmount}
                      onChange={(e) => setMaxShippingCapAmount(e.target.value)}
                      onBlur={() => setMaxShippingCapAmount(formatMoney(maxShippingCapAmount))}
                      style={styles.inputWithPrefixField}
                      placeholder="25.00"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderStrategyCard(
    value: ShippingStrategy,
    Icon: typeof Truck,
    title: string,
    desc: string,
  ) {
    const active = strategy === value;
    return (
      <button
        key={value}
        type="button"
        onClick={() => setStrategy(value)}
        style={{
          ...styles.strategyCard,
          ...(active ? styles.strategyCardActive : {}),
        }}
      >
        <div style={{
          ...styles.strategyCardIcon,
          backgroundColor: active ? '#5F3DC4' : colors.gray[100],
        }}>
          <Icon size={18} color={active ? '#FFFFFF' : colors.gray[600]} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.strategyCardTitle}>{title}</div>
          <div style={styles.strategyCardDesc}>{desc}</div>
        </div>
        {active && (
          <div style={styles.checkCircle}>
            <Check size={14} color="#FFFFFF" />
          </div>
        )}
      </button>
    );
  }

  function renderAddressPane() {
    return (
      <>
        <div style={styles.paneHeader}>
          <div style={styles.paneIconCircle}>
            <MapPin size={20} color="#FFFFFF" />
          </div>
          <div>
            <h3 style={styles.paneTitle}>Return Address</h3>
            <p style={styles.paneDesc}>Printed as the return address on every shipping label you buy.</p>
          </div>
        </div>

        <div style={styles.addressForm}>
          <div style={styles.addressFormRow2}>
            <div>
              <label style={styles.fieldLabel}>Name / Business</label>
              <input
                type="text"
                value={originName}
                onChange={(e) => setOriginName(e.target.value)}
                style={styles.input}
                placeholder="Jane's Collectibles"
              />
            </div>
          </div>

          <div style={styles.addressFormRow2}>
            <div>
              <label style={styles.fieldLabel}>Street Address</label>
              <input
                type="text"
                value={originStreet1}
                onChange={(e) => setOriginStreet1(e.target.value)}
                style={styles.input}
                placeholder="123 Main St"
              />
            </div>
          </div>

          <div style={styles.addressFormRow2}>
            <div>
              <label style={styles.fieldLabel}>
                Apt / Suite <span style={styles.optionalTag}>Optional</span>
              </label>
              <input
                type="text"
                value={originStreet2}
                onChange={(e) => setOriginStreet2(e.target.value)}
                style={styles.input}
                placeholder="Suite 100"
              />
            </div>
          </div>

          <div style={styles.addressFormRow3}>
            <div>
              <label style={styles.fieldLabel}>City</label>
              <input
                type="text"
                value={originCity}
                onChange={(e) => setOriginCity(e.target.value)}
                style={styles.input}
                placeholder="Los Angeles"
              />
            </div>
            <div>
              <label style={styles.fieldLabel}>State</label>
              <select
                value={originState}
                onChange={(e) => setOriginState(e.target.value)}
                style={styles.select}
              >
                <option value="">--</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={styles.fieldLabel}>ZIP</label>
              <input
                type="text"
                value={originZip}
                onChange={(e) => setOriginZip(e.target.value)}
                style={styles.input}
                placeholder="90001"
                maxLength={10}
              />
            </div>
          </div>

          <div style={styles.addressFormRow2}>
            <div>
              <label style={styles.fieldLabel}>Phone</label>
              <input
                type="tel"
                value={originPhone}
                onChange={(e) => setOriginPhone(e.target.value)}
                style={styles.input}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderLabelPane() {
    return (
      <>
        <div style={styles.paneHeader}>
          <div style={styles.paneIconCircle}>
            <Printer size={20} color="#FFFFFF" />
          </div>
          <div>
            <h3 style={styles.paneTitle}>Label Format</h3>
            <p style={styles.paneDesc}>Match this to your printer so labels come out the right size.</p>
          </div>
        </div>

        <div style={styles.labelGrid}>
          {/* Letter */}
          <button
            type="button"
            onClick={() => setLabelFormat('LETTER')}
            style={{
              ...styles.labelCard,
              ...(labelFormat === 'LETTER' ? styles.labelCardActive : {}),
            }}
          >
            <div style={styles.labelPreview}>
              <div style={{
                width: '40px',
                height: '52px',
                border: `2px solid ${labelFormat === 'LETTER' ? '#5F3DC4' : colors.gray[300]}`,
                borderRadius: '4px',
                backgroundColor: labelFormat === 'LETTER' ? 'rgba(95,61,196,0.06)' : colors.gray[100],
              }} />
            </div>
            <div style={styles.labelCardInfo}>
              <span style={styles.labelCardTitle}>Letter (8.5 x 11)</span>
              <span style={styles.labelCardDesc}>Standard paper \u2014 print and cut</span>
            </div>
            {labelFormat === 'LETTER' && (
              <div style={styles.checkCircle}>
                <Check size={14} color="#FFFFFF" />
              </div>
            )}
          </button>

          {/* Thermal */}
          <button
            type="button"
            onClick={() => setLabelFormat('THERMAL')}
            style={{
              ...styles.labelCard,
              ...(labelFormat === 'THERMAL' ? styles.labelCardActive : {}),
            }}
          >
            <div style={styles.labelPreview}>
              <div style={{
                width: '52px',
                height: '36px',
                border: `2px solid ${labelFormat === 'THERMAL' ? '#5F3DC4' : colors.gray[300]}`,
                borderRadius: '4px',
                backgroundColor: labelFormat === 'THERMAL' ? 'rgba(95,61,196,0.06)' : colors.gray[100],
              }} />
            </div>
            <div style={styles.labelCardInfo}>
              <span style={styles.labelCardTitle}>Thermal (4 x 6)</span>
              <span style={styles.labelCardDesc}>For thermal label printers</span>
            </div>
            {labelFormat === 'THERMAL' && (
              <div style={styles.checkCircle}>
                <Check size={14} color="#FFFFFF" />
              </div>
            )}
          </button>
        </div>
      </>
    );
  }
}

// ── Styles ──────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  // Page
  page: {},
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
    color: colors.ink.black,
  },
  pageSubtitle: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: '4px 0 0 0',
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '40px',
    color: colors.gray[600],
  },
  errorBanner: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  successBanner: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: '#D1FAE5',
    color: '#059669',
    borderRadius: '8px',
    fontSize: '14px',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#5F3DC4',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  // Layout
  layout: {
    display: 'flex',
    gap: '24px',
  },

  // Sidebar
  sidebar: {
    width: '300px',
    flexShrink: 0,
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '8px',
    position: 'sticky',
    top: '24px',
    alignSelf: 'flex-start',
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '12px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s',
  },
  sidebarItemActive: {
    backgroundColor: 'rgba(95, 61, 196, 0.08)',
  },
  sidebarIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sidebarText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarLabel: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '1.3',
  },
  sidebarStatus: {
    fontSize: '12px',
    color: colors.gray[600],
    lineHeight: '1.3',
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Pane
  pane: {
    flex: 1,
    minWidth: 0,
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    padding: '28px',
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '28px',
    paddingBottom: '24px',
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  paneIconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: '#5F3DC4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  paneTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  paneDesc: {
    fontSize: '13px',
    color: colors.gray[600],
    margin: '4px 0 0 0',
    lineHeight: '1.4',
  },

  // Pricing grid
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
  },

  // Step header
  stepHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '16px',
  },
  stepTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  stepDesc: {
    fontSize: '12px',
    color: colors.gray[600],
    marginTop: '2px',
  },

  // Strategy cards
  strategyCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  strategyCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px',
    backgroundColor: colors.white,
    // Longhand so React can independently restore borderColor when the
    // *Active variant is removed (shorthand `border` strips the longhand
    // and leaves currentColor as the fallback).
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
  },
  strategyCardActive: {
    borderColor: '#5F3DC4',
    backgroundColor: 'rgba(95, 61, 196, 0.04)',
  },
  strategyCardIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  strategyCardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  strategyCardDesc: {
    fontSize: '12px',
    color: colors.gray[600],
    lineHeight: '1.4',
    marginTop: '2px',
  },
  checkCircle: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#5F3DC4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Strategy drawer (sub-section)
  strategyDrawer: {
    marginTop: '12px',
    backgroundColor: '#F4F3F8',
    borderRadius: '12px',
    padding: '16px',
  },
  drawerField: {
    marginBottom: '16px',
  },

  // Carrier cards
  carrierRow: {
    display: 'flex',
    gap: '10px',
  },
  carrierCard: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: colors.white,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s',
  },
  carrierCardActive: {
    borderColor: '#5F3DC4',
    backgroundColor: 'rgba(95, 61, 196, 0.04)',
  },

  rateMeta: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  testRatesLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '12px',
    padding: 0,
    border: 'none',
    background: 'none',
    fontSize: '13px',
    fontWeight: 500,
    color: '#5F3DC4',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },


  akHiWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '12px 16px',
    marginTop: '8px',
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: '1.4',
  },

  // Toggle cards
  toggleCard: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '12px',
    padding: '16px',
    transition: 'all 0.15s',
  },
  toggleCardActive: {
    borderColor: '#5F3DC4',
    backgroundColor: 'rgba(95, 61, 196, 0.04)',
  },
  toggleCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  toggleCardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  toggleCardDesc: {
    fontSize: '12px',
    color: colors.gray[600],
    marginTop: '4px',
    lineHeight: '1.4',
  },
  toggleCardBody: {
    marginTop: '14px',
    paddingTop: '14px',
    borderTop: `1px solid ${colors.gray[200]}`,
  },

  // Shared form
  fieldLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.gray[700],
    marginBottom: '6px',
  },
  fieldHelper: {
    display: 'block',
    fontSize: '12px',
    color: colors.gray[600],
    marginTop: '6px',
    lineHeight: '1.4',
  },
  optionalTag: {
    fontSize: '11px',
    fontWeight: 400,
    color: colors.gray[600],
    marginLeft: '4px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
    appearance: 'auto',
  },
  inputWithPrefix: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  inputPrefix: {
    position: 'absolute',
    left: '12px',
    fontSize: '14px',
    color: colors.gray[600],
  },
  inputWithPrefixField: {
    width: '120px',
    padding: '10px 12px 10px 24px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
  },

  // Address form
  addressForm: {
    maxWidth: '640px',
  },
  addressFormRow2: {
    marginBottom: '14px',
  },
  addressFormRow3: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    gap: '12px',
    marginBottom: '14px',
  },

  // Label format
  labelGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  labelCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '24px 16px',
    backgroundColor: colors.white,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.15s',
    position: 'relative',
  },
  labelCardActive: {
    borderColor: '#5F3DC4',
    backgroundColor: 'rgba(95, 61, 196, 0.04)',
  },
  labelPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60px',
  },
  labelCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  labelCardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  labelCardDesc: {
    fontSize: '12px',
    color: colors.gray[600],
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    padding: '24px',
  },
  confirmText: {
    fontSize: '14px',
    color: colors.gray[600],
    marginTop: 0,
    marginBottom: '20px',
  },
  comparisonRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  addressOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  addressOptionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  addressCard: {
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.gray[100],
    flex: 1,
  },
  suggestedCard: {
    borderColor: '#5F3DC4',
    backgroundColor: colors.brand.blue08,
  },
  addressLine: {
    fontSize: '14px',
    color: colors.ink.black,
    lineHeight: '1.5',
  },
  diffLine: {
    fontSize: '14px',
    color: colors.ink.black,
    lineHeight: '1.5',
    fontWeight: 700,
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFFFF',
    backgroundColor: '#5F3DC4',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};
